import React, { useEffect, useRef, useState } from "react";
import { Avatar } from "@mui/material";
import api from "../../utils/api";
import "./ChatComponent.css";

import iconSend from "../../assets/icone-enviar.png";
import iconClose from "../../assets/Vector (32).png";
import moment from "moment/moment";
import TalentAvatar from "../TalentAvatar/TalentAvatar";
import CheckMessage from "./CheckMessage";

const DetailChat = ({ id, messageId, nameTalents, data, isActive, onOpenChat, onCloseChat, activeChats, isCollapsed, onCollapseChat, isContactsListOpen, inList, activeChatIds, unreadCount, lastMessageUpdate, viewedContacts }) => {
  const [isFetching, setIsFetching] = useState(false);
  const [menssages, setMenssages] = useState([]);
  const [message, setMessage] = useState("");
  const inputRef = useRef(null);
  const [previousMessagesLength, setPreviousMessagesLength] = useState(0);
  const [isUserAtBottom, setIsUserAtBottom] = useState(true);

  const messagesEndRef = useRef(null);
  const messagesContainerRef = useRef(null);

  const fetchMessages = async () => {
    if (!isFetching) {
      if (!messageId) return;

      setIsFetching(true);

      try {
        const { data } = await api.get(`/companies/messages/${messageId}/`);

        const sortedMessages = data?.data.sort(function (a, b) {
          if (a.attributes.createdAt < b.attributes.createdAt) return -1;
          if (a.attributes.createdAt > b.attributes.createdAt) return 1;
          return 0;
        });

        setPreviousMessagesLength(menssages.length);
        setMenssages(sortedMessages);
      } catch (error) {
        console.error(error);
      } finally {
        setIsFetching(false);
      }
    }
  };

  useEffect(() => {
    // Only fetch messages if the chat window is actually open and not collapsed
    if (isActive && !inList && !isCollapsed) {
      fetchMessages();
    }
  }, [isActive, inList, isCollapsed]);

  useEffect(() => {
    let intervalId = null;
    // Only poll for messages if the chat window is actually open and not collapsed
    if (isActive && !inList && !isCollapsed) {
      intervalId = setInterval(fetchMessages, 2000);
    }
    return () => {
      if (intervalId) {
        clearInterval(intervalId);
      }
    };
  }, [isActive, isFetching, inList, isCollapsed]);

  const createInitialMessage = async (text) => {
    if (!isFetching) {
      setIsFetching(true);
      try {
        const { data: responseData } = await api.post(`/companies/messages/`, {
          data: {
            type: "messages",
            attributes: {
              receiver_id: data?.attributes?.receiver?.id,
              receiver_type: "Talent",
              text_msg: text,
            },
          },
        });
        setMenssages([responseData.data]);
        setMessage("");
        fetchMessages();
      } catch (error) {
        console.error(error);
      } finally {
        setIsFetching(false);
      }
    }
  };

  const sendMessage = async () => {
    if (!message.trim()) return;

    if (!messageId) {
      // New chat, create initial message
      await createInitialMessage(message);
      return;
    }

    const newMessage = {
      id: `temp-${Date.now()}`,
      attributes: {
        sender: { type: "Company" },
        textMsg: message,
        createdAt: new Date().toISOString(),
        receiver: data?.attributes?.receiver,
        readed: false
      }
    };

    // Add message optimistically
    setMenssages(prev => [...prev, newMessage]);
    setMessage("");

    // Always scroll to bottom when user sends a message
    setIsUserAtBottom(true);

    try {
      const response = await api.post(`/companies/messages/`, {
        type: "messages",
        data: {
          attributes: {
            receiver_id: data?.attributes?.receiver?.id,
            receiver_type: "Talent",
            text_msg: message,
          },
        },
      });

      // Update message with server response
      setMenssages(prev => prev.map(msg =>
        msg.id === newMessage.id ? { ...response.data, attributes: { ...response.data.attributes, readed: false } } : msg
      ));
    } catch (error) {
      console.log(error);
      // Remove temporary message in case of error
      setMenssages(prev => prev.filter(msg => msg.id !== newMessage.id));
      alert("Something unexpected happened. Please try again.");
    }
  };

  const handleKeyPress = (event) => {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      sendMessage();
    }
  };

  const checkIfUserAtBottom = () => {
    if (messagesContainerRef.current) {
      const { scrollTop, scrollHeight, clientHeight } = messagesContainerRef.current;
      const threshold = 100; // 100px threshold
      setIsUserAtBottom(scrollTop + clientHeight >= scrollHeight - threshold);
    }
  };

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(() => {
    // Only scroll to bottom if there are new messages and user was at bottom
    const hasNewMessages = menssages.length > previousMessagesLength;
    if (hasNewMessages && isUserAtBottom) {
      scrollToBottom();
    }
  }, [menssages, previousMessagesLength, isUserAtBottom]);

  const toggleCollapse = () => {
    onCollapseChat(id, !isCollapsed);
  };

  useEffect(() => {
    if (isActive && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isActive]);

  // Force update when new messages arrive globally
  useEffect(() => {
    // Only fetch messages if the chat window is actually open and not collapsed
    if (isActive && !inList && !isCollapsed && lastMessageUpdate) {
      fetchMessages();
    }
  }, [lastMessageUpdate, isActive, inList, isCollapsed]);

  return (
    <>
      {/* Render contact only if NOT open */}
      {inList && (
        <div
          key={data?.attributes?.receiver?.id}
          className={`contact-item ${
            (unreadCount > 0 && !viewedContacts?.has(id)) || activeChatIds.includes(id) ? "active" : ""
          }`}
          onClick={() => onOpenChat(data?.attributes?.receiver?.id)}
        >
          <div className="containerIconProfile">
            <TalentAvatar
              talent={data?.attributes?.receiver}
              width="40px"
              height="40px"
              background="#EC5569"
              permitLightbox={false}
            />
          </div>
          <div className="contact-info">
            <div className="contact-name" title={data?.attributes?.receiver?.name}>
              {data?.attributes?.receiver?.name}
            </div>
            <div className="contact-status">{data?.attributes?.textMsg}</div>
            <div className="contact-time">
              {moment(data?.attributes?.createdAt).format("HH:mm")}
            </div>
          </div>
          {unreadCount > 0 && (
            <div className="contact-unread-badge">
              {unreadCount > 99 ? '99+' : unreadCount}
            </div>
          )}
        </div>
      )}

      {/* Render chat window only if open */}
      {isActive && (
        <div
          className={`chat-window ${isCollapsed ? 'collapsed' : ''}`}
          style={{
            right: `${(isContactsListOpen ? 290 : 220) + (activeChatIds.indexOf(id) * 270)}px`
          }}
        >
          {/* Chat Header */}
          <div
            className="chat-header"
            onClick={(e) => {
              e.stopPropagation();
              toggleCollapse();
            }}
          >
            <div className="user-info">
              <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
                <div style={{ position: 'relative', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <TalentAvatar
                    talent={data?.attributes?.receiver}
                    width="25px"
                    height="25px"
                    background="#EC5569"
                  />
                  {isCollapsed && unreadCount > 0 && (
                    <div className="unread-badge">
                      {unreadCount > 99 ? '99+' : unreadCount}
                    </div>
                  )}
                </div>
                <span className="user-name" title={nameTalents}>{nameTalents}</span>
              </div>
            </div>
            <button className="collapse-button" style={{background: 'none', border: 'none', cursor: 'pointer', padding: 0, marginRight: 0, height: '100%', display: 'flex', alignItems: 'center'}} onClick={e => { e.stopPropagation(); toggleCollapse(); }}>
              {isCollapsed ? (
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M6 9L12 15L18 9" stroke="#000" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              ) : (
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M6 15L12 9L18 15" stroke="#000" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              )}
            </button>
            <button
              className="close-button"
              onClick={(e) => {
                e.stopPropagation();
                onCloseChat(id);
              }}
            >
              <img src={iconClose} alt="" className="iconClose" />
            </button>
          </div>

          {!isCollapsed && (
            <>
              {/* Chat Messages */}
              <div className="chat-messages" ref={messagesContainerRef} onScroll={checkIfUserAtBottom}>
                {menssages?.map((msg) => (
                  <div
                    key={msg?.id}
                    className={`message ${
                      msg?.attributes?.sender?.type === "Company"
                        ? "user-message"
                        : "contact-message"
                    }`}
                  >
                    <div className="message-content">
                      <div className="message-text">{msg?.attributes?.textMsg}</div>
                      <div className="message-time">
                        {moment(msg?.attributes?.createdAt).format("HH:mm")}
                        {msg?.attributes?.sender?.type === "Company" && (
                          <div className={`message-status`}>
                            <CheckMessage
                              color="currentColor"
                              readed={msg?.attributes?.readed}
                            />
                          </div>
                        )}
                      </div>
                      <div ref={messagesEndRef} />
                    </div>
                  </div>
                ))}
              </div>

              {/* Scroll to bottom button - only show when user is not at bottom */}
              {!isUserAtBottom && (
                <button
                  className="scroll-to-bottom-btn-detail"
                  onClick={() => {
                    setIsUserAtBottom(true);
                    scrollToBottom();
                  }}
                >
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                    <path
                      d="M7 10L12 15L17 10"
                      stroke="white"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </button>
              )}

              {/* Chat Input */}
              <div className="chat-input">
                <input
                  ref={inputRef}
                  type="text"
                  placeholder="Mensagem"
                  value={message || ""}
                  onChange={(event) => setMessage(event.target.value)}
                  onKeyPress={handleKeyPress}
                />
                <button className="send-button" onClick={sendMessage}>
                  <img src={iconSend} alt="Enviar" />
                </button>
              </div>
            </>
          )}
        </div>
      )}
    </>
  );
};

export default DetailChat;
